<script lang="ts" setup>
import { ButtonSize, ButtonStyle, ButtonType } from '~/static/enums/components/button'

interface Props {
  buttonType?: ButtonType
  buttonSize?: ButtonSize
  buttonStyle?: ButtonStyle
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  buttonType: ButtonType.Button,
  buttonSize: ButtonSize.Medium,
  buttonStyle: ButtonStyle.Primary,
  disabled: false,
})

const btnClass = computed<string>(() => {
  return props.buttonStyle ?? ''
})

const btnSize = computed<string>(() => {
  return props.buttonSize ?? ''
})

</script>

<template>
  <button
    :class="`btn ${btnClass} ${btnSize}`"
    :disabled="disabled"
    :type="buttonType"
  >
    <slot />
  </button>
</template>

<style lang="scss" scoped>
.btn {
  font-weight: 400;
  font-size: rem(15px);
  line-height: rem(20px);
  padding-top: rem(11px);
  padding-bottom: rem(11px);
  border-radius: rem(8px);

  &:disabled {
    pointer-events: none;
  }

  // .button_primary
  &_primary {
    background: $bg-accent;
    border-color: transparent;
    color: $text-primary;

    @media (any-hover: hover) {
      &:hover {
        background: $bg-accent-hover;
        border-color: transparent;
      }
    }

    &:active {
      background: $bg-accent-active;
      border-color: transparent;
      color: $text-primary;
    }

    &:disabled {
      opacity: .4;
    }
  }

  // .button_secondary
  &_secondary {
    background: $bg-neutral;
    border-color: transparent;
    color: $text-action;

    @media (any-hover: hover) {
      &:hover {
        background: $bg-neutral-hover;
        border-color: transparent;
      }
    }

    &:active {
      background: $bg-neutral-active;
      border-color: transparent;
      color: $text-action;
    }

    &:disabled {
      opacity: .65;
    }
  }

  // .button_white
  &_white {
    background: transparent;
    border-color: transparent;
    color: $text-action;

    @media (any-hover: hover) {
      &:hover {
        background: $bg-neutral;
        border-color: transparent;
      }
    }

    &:active {
      background: $bg-neutral-active;
      border-color: transparent;
      color: $text-action;
    }

    &:disabled {
      opacity: .65;
    }
  }

  // .button_outline-primary
  &_outline-primary {
    border-color: $indigo-500;
    color: $indigo-500;

    @media (any-hover: hover) {
      &:hover {
        border-color: $indigo-500;
        background: $indigo-500;
        color: $white;
      }
    }

    &:active {
      border-color: $indigo-500;
      color: $white;
    }

    &:disabled {
      opacity: .65;
    }
  }

  // .button_outline-secondary
  &_outline-secondary {
    border-color: $indigo-100-light;
    color: $indigo-500;

    @media (any-hover: hover) {
      &:hover {
        border-color: $indigo-100-light;
        background: $indigo-100-light;
        color: $indigo-500;
      }
    }

    &:active {
      border-color: $indigo-100-light;
      background: $indigo-100-light;
      color: $indigo-500;
    }

    &:disabled {
      color: $indigo-100-light;
      opacity: .65;
    }
  }

  // .button_outline-light
  &_outline-light {
    border-color: $white;
    color: $white;

    @media (any-hover: hover) {
      &:hover {
        background: $white;
        color: $black;
      }
    }

    &:disabled {
      opacity: .65;
    }

    // .button_light
    &_light {
      border: none;
      color: $dark;

      &:disabled {
        opacity: .65;
      }
    }

    // .button_link
    &_link {
      border: none;
      color: $indigo-500;

      @media (any-hover: hover) {
        &:hover {
          color: $indigo-800;
        }
      }

      &:active {
        color: $indigo-800;
      }

      &:disabled {
        color: $gray-600;
      }
    }
  }

  // .button_danger
  &_danger {
    background: $red-500;
    border-color: $red-500;
    color: $white;

    @media (any-hover: hover) {
      &:hover {
        background: $red-600;
        border-color: $red-600;
      }
    }

    &:active {
      background: $red-600;
      color: $white;
      border-color: $red-600;
    }

    &:disabled {
      background: $red-500;
      border-color: $red-500;
      opacity: .65;
    }
  }
}
</style>
